import { FC } from 'react';
import { ACWR_ZONES } from '@leopart/calc';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';
import dayjs from 'dayjs';
import { IAcwrSeriesMetric } from '../../../../models/injury';
import { ChartEmptyState } from './_shared/ChartEmptyState';
import {
  chartAxisTick,
  chartGridProps,
  chartTooltipContentStyle,
  chartTooltipItemStyle,
  chartTooltipLabelStyle,
} from '../../../../shared/charts/chartTheme';

interface Props {
  data: IAcwrSeriesMetric | null;
  period?: string;
  height?: number;
}

export const AcwrSeriesChart: FC<Props> = ({ data, height }) => {
  if (!data || !data.series || data.series.length === 0) {
    return <ChartEmptyState />;
  }

  return (
    <ResponsiveContainer width="100%" height={height ?? 220}>
      <LineChart data={data.series} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <ReferenceArea y1={0} y2={ACWR_ZONES.okMin} fill="var(--c-error)" fillOpacity={0.08} />
        <ReferenceArea y1={ACWR_ZONES.okMin} y2={ACWR_ZONES.warnMax} fill="var(--ri-good)" fillOpacity={0.1} />
        <ReferenceArea y1={ACWR_ZONES.warnMax} y2={5} fill="var(--ri-warning)" fillOpacity={0.12} />
        <CartesianGrid {...chartGridProps} />
        <XAxis dataKey="date" tickFormatter={(v) => dayjs(v).format('DD/MM')} tick={chartAxisTick} />
        <YAxis domain={[0, 'auto']} tick={chartAxisTick} />
        <Tooltip
          contentStyle={chartTooltipContentStyle}
          labelStyle={chartTooltipLabelStyle}
          itemStyle={chartTooltipItemStyle}
          formatter={(_value, _name, props) => {
            const { date, acwr, acute, chronic } = props.payload;
            return [
              `ACWR: ${acwr?.toFixed(2)} | Acuto: ${acute} | Cronico: ${chronic}`,
              dayjs(date).format('DD/MM/YYYY'),
            ];
          }}
        />
        <Line type="monotone" dataKey="acwr" stroke="var(--c-text-0)" strokeWidth={2} dot={false} />
        {data.injuryMarkers?.map((marker) => (
          <ReferenceLine key={marker.injuryId} x={marker.date} stroke="var(--ri-critical)" strokeWidth={1.5} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};
